// src/components/CaseStudies/CaseStudyDetail.tsx
import type { CaseStudy } from "../../data/caseStudies";

interface CaseStudyDetailProps {
  caseStudy: CaseStudy | null;
}

const CaseStudyDetail: React.FC<CaseStudyDetailProps> = ({ caseStudy }) => {
  if (!caseStudy) {
    return (
      <div className="border border-dashed border-line-strong p-6 text-sm text-muted">
        <p>Select a case study on the left to see a detailed breakdown.</p>
      </div>
    );
  }

  return (
    <div className="surface-card">
      <div className="p-6 sm:p-7 space-y-5">
        <div>
          <p className="kicker mb-2">Case study</p>
          <h2 className="font-display text-lg sm:text-xl font-semibold text-ink mb-1">
            {caseStudy.title}
          </h2>
          <p className="text-xs text-muted">
            {caseStudy.client} · {caseStudy.sector}
            {caseStudy.location ? ` · ${caseStudy.location}` : ""}{" "}
            {caseStudy.year ? `· ${caseStudy.year}` : ""}
          </p>
        </div>

        <div className="space-y-1.5">
          <h3 className="kicker">Summary</h3>
          <p className="text-sm text-ink">{caseStudy.summary}</p>
        </div>

        <div className="space-y-1.5">
          <h3 className="kicker">Challenge</h3>
          <p className="text-sm text-ink">{caseStudy.challenge}</p>
        </div>

        <div className="space-y-1.5">
          <h3 className="kicker">Approach</h3>
          <ul className="space-y-1.5 text-sm text-ink">
            {caseStudy.approach.map((item, idx) => (
              <li key={idx} className="flex gap-2.5">
                <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-1.5">
          <h3 className="kicker">Outcomes</h3>
          <ul className="space-y-1.5 text-sm text-ink">
            {caseStudy.outcomes.map((item, idx) => (
              <li key={idx} className="flex gap-2.5">
                <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {caseStudy.impactMetrics.length > 0 && (
        <div className="border-t border-line px-6 sm:px-7 py-5">
          <h3 className="kicker mb-3">Outcome highlights</h3>
          <ul className="grid sm:grid-cols-3 gap-x-6 gap-y-2.5">
            {caseStudy.impactMetrics.map((metric) => (
              <li key={metric} className="flex gap-2.5 text-sm text-ink leading-relaxed">
                <span className="mt-1.5 h-1 w-1 flex-none bg-signal" />
                <span>{metric}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="px-6 sm:px-7 py-4 border-t border-line flex flex-wrap gap-2">
        {caseStudy.tags.map((tag) => (
          <span key={tag} className="tag-chip">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
};

export default CaseStudyDetail;
